import { ReactElement } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { Spinner } from '@/components/ui/spinner'
import {
  faBuilding,
  faCamera,
  faBell,
  faCircleCheck,
  faWind,
  faArrowRight,
  faMobileScreen,
  faEye,
} from '@awesome.me/kit-e215ba4646/icons/sharp/light'
import { Link, Navigate } from 'react-router-dom'
import { useSession } from '../contexts/SessionContext'
import Footer from '../components/Footer'

function isPwaLaunch(): boolean {
  if (typeof window === 'undefined') return false
  // Standard: display-mode media query is 'standalone' when launched from
  // the installed home-screen entry. Covers Chrome / Edge / Safari PWAs.
  const standardMatch =
    window.matchMedia?.('(display-mode: standalone)')?.matches ?? false
  // iOS legacy: Apple exposes navigator.standalone for Safari home-screen
  // launches before / outside the display-mode standard.
  const iosLegacy =
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true
  return standardMatch || iosLegacy
}

export default function Home(): ReactElement | null {
  const { isAuthenticated, loading, isInitialized } = useSession()

  // Wait for auth state to settle before making routing decisions
  // This prevents the Sign In button from being non-interactive after logout
  if (!isInitialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Spinner className="size-8 text-emerald-600" />
      </div>
    )
  }

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  // PWA home-screen launch as unauthenticated: skip the marketing landing
  // and route straight to /signin. Web visitors (not PWA-installed) still
  // see the marketing page below — preserves the public-facing pitch while
  // optimizing the installed-app flow for repeat sign-in attempts.
  if (isPwaLaunch()) {
    return <Navigate to="/signin" replace />
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Sticky Navigation Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Icon icon={faWind} className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-gray-900">MNTND</span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="#features"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Features
              </Link>
              <Link
                to="#benefits"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Benefits
              </Link>
              <Link
                to="#pricing"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Pricing
              </Link>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:bg-white dark:border-gray-300 dark:text-gray-700 dark:hover:bg-gray-100"
                asChild
              >
                <Link to="/signin">Sign In</Link>
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                asChild
              >
                <Link to="/signup">Start Free Trial</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1">
        {/* Hero Section - Bee.computer inspired bold layout */}
        <div className="container mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-5xl mx-auto">
            <div className="text-center space-y-8 mb-16">
              <Badge
                variant="outline"
                className="border-emerald-200 text-emerald-700 bg-emerald-50 text-sm"
              >
                🌬️ Introducing MNTND
              </Badge>

              <h1 className="text-6xl lg:text-7xl font-light tracking-tight text-gray-900 leading-[0.9]">
                <div className="mb-4">The property</div>
                <div className="mb-4 font-medium text-emerald-600">
                  maintenance solution
                </div>
                <div>that works</div>
              </h1>

              <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
                MNTND transforms your HVAC maintenance into automated tenant
                engagement. Visual verification you can trust. Perfect for
                property owners in hot climates.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
                <Button
                  size="lg"
                  className="h-14 px-10 bg-emerald-600 hover:bg-emerald-700 text-white text-lg"
                  asChild
                >
                  <Link to="/signup" className="flex items-center gap-2">
                    Start Free Trial
                    <Icon icon={faArrowRight} className="h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 px-10 text-lg border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:bg-white dark:border-gray-300 dark:text-gray-700 dark:hover:bg-gray-100"
                  asChild
                >
                  <Link to="#features">Learn More</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Narrative Sections - Bee.computer style storytelling */}
        <div id="features" className="bg-gray-50 py-24">
          <div className="container mx-auto px-6">
            {/* Section 1 - Meaning */}
            <div className="max-w-6xl mx-auto mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="space-y-8">
                  <h2 className="text-4xl lg:text-5xl font-light text-gray-900 leading-tight">
                    <div>MNTND turns your</div>
                    <div className="font-medium text-emerald-600">
                      maintenance chaos
                    </div>
                    <div>into peace of mind</div>
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    No more late-night emergency calls. No more wondering if
                    maintenance was actually done. MNTND creates a seamless
                    system where tenants become your maintenance partners
                    through automated notifications and photo verification.
                  </p>
                </div>
                <div className="bg-white rounded-3xl p-8 shadow-sm">
                  <div className="aspect-square bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-2xl flex items-center justify-center">
                    <Icon
                      icon={faCamera}
                      className="h-24 w-24 text-emerald-600"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 - Learning */}
            <div className="max-w-6xl mx-auto mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="bg-white rounded-3xl p-8 shadow-sm lg:order-1 order-2">
                  <div className="aspect-square bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center">
                    <Icon icon={faBell} className="h-24 w-24 text-blue-600" />
                  </div>
                </div>
                <div className="space-y-8 lg:order-2 order-1">
                  <h2 className="text-4xl lg:text-5xl font-light text-gray-900 leading-tight">
                    <div>MNTND learns and</div>
                    <div className="font-medium text-emerald-600">
                      grows with your properties
                    </div>
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Our intelligent system tracks maintenance cycles, sends
                    timely reminders, and builds a complete history of your
                    property care. The more you use it, the smarter it gets at
                    predicting your maintenance needs.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3 - Ready */}
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="space-y-8">
                  <h2 className="text-4xl lg:text-5xl font-light text-gray-900 leading-tight">
                    <div>MNTND is ready</div>
                    <div className="font-medium text-emerald-600">
                      when you need it
                    </div>
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    24/7 monitoring of your maintenance schedules. Instant
                    notifications when action is needed. Real-time photo
                    verification from tenants. Everything you need to keep your
                    properties running smoothly, automatically.
                  </p>
                </div>
                <div className="bg-white rounded-3xl p-8 shadow-sm">
                  <div className="aspect-square bg-gradient-to-br from-purple-100 to-purple-50 rounded-2xl flex items-center justify-center">
                    <Icon
                      icon={faCircleCheck}
                      className="h-24 w-24 text-purple-600"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bento Grid Features - Inspired by bee.computer's grid layout */}
        <div id="benefits" className="py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl lg:text-5xl font-light text-gray-900 text-center mb-16 leading-tight">
                Built for modern property owners
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Large feature card */}
                <div className="md:col-span-2 lg:col-span-2 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-8 text-white">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <Icon icon={faWind} className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-medium">
                      Prevent System Failures
                    </h3>
                    <p className="text-emerald-100 text-lg leading-relaxed">
                      Clean filters mean efficient HVAC systems. Reduce
                      emergency repairs by 80% with proactive maintenance
                      scheduling.
                    </p>
                  </div>
                </div>

                {/* Small feature card */}
                <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                      <Icon
                        icon={faMobileScreen}
                        className="h-8 w-8 text-blue-600"
                      />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900">
                      Mobile First
                    </h3>
                    <p className="text-gray-600">
                      Tenants use their phones to submit maintenance photos
                      instantly.
                    </p>
                  </div>
                </div>

                {/* Medium feature card */}
                <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center">
                      <Icon icon={faEye} className="h-8 w-8 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900">
                      Visual Verification
                    </h3>
                    <p className="text-gray-600">
                      Photo proof that maintenance was completed properly, every
                      time.
                    </p>
                  </div>
                </div>

                {/* Large feature card */}
                <div className="md:col-span-2 lg:col-span-2 bg-gray-900 rounded-3xl p-8 text-white">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
                      <Icon icon={faBuilding} className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-medium">
                      Scale Across Properties
                    </h3>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      Manage maintenance for 1 property or 1,000. MNTND grows
                      with your portfolio and keeps everything organized in one
                      dashboard.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section - Bee.computer inspired */}
        <div id="pricing" className="bg-gray-900 py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl lg:text-5xl font-light text-white mb-8 leading-tight">
                Ready to transform your maintenance?
              </h2>
              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                Join property owners who&apos;ve eliminated maintenance
                headaches with MNTND.
              </p>

              <div className="space-y-4">
                <Button
                  size="lg"
                  className="h-14 px-10 bg-emerald-600 hover:bg-emerald-700 text-white text-lg"
                  asChild
                >
                  <Link to="/signup" className="flex items-center gap-2">
                    Start Free Trial
                    <Icon icon={faArrowRight} className="h-5 w-5" />
                  </Link>
                </Button>
                <p className="text-gray-400 text-sm">
                  No credit card required &bull; 14-day free trial
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
